// @ts-nocheck
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { Button } from "@libraryadmin/components/ui/button";
import { usePagination } from "@libraryadmin/hooks/usePagination";
import { DataTablePagination } from "@libraryadmin/components/ui/data-table-pagination";
import { Input } from "@libraryadmin/components/ui/input";
import { Label } from "@libraryadmin/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@libraryadmin/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@libraryadmin/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@libraryadmin/components/ui/select";
import { Badge } from "@libraryadmin/components/ui/badge";
import { Skeleton } from "@libraryadmin/components/ui/skeleton";
import {
  UserPlus,
  Search,
  Edit,
  Eye,
  Phone,
  Mail,
  Trash2,
  AlertCircle,
  QrCode,
  ArrowLeft,
  Save,
  User,
  MapPin,
  Calendar,
  CreditCard,
} from "lucide-react";
import { useToast } from "@libraryadmin/hooks/use-toast";
import {
  getMembers,
  createMember,
  updateMember,
  deleteMember,
} from "@libraryadmin/services/api";
import type { Member } from "@libraryadmin/data/seed";
import { MemberIDCardModal } from "@libraryadmin/components/members/MemberIDCardModal";

// ─── Indian States list ───────────────────────────────────────────────────────
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Delhi",
  "Jammu & Kashmir",
  "Ladakh",
  "Puducherry",
  "Chandigarh",
];

// ─── Gujarat cities (expandable) ─────────────────────────────────────────────
const CITIES_BY_STATE: Record<string, string[]> = {
  Gujarat: [
    "Ahmedabad",
    "Surat",
    "Vadodara",
    "Rajkot",
    "Bhavnagar",
    "Jamnagar",
    "Junagadh",
    "Gandhinagar",
    "Anand",
    "Nadiad",
    "Mehsana",
    "Morbi",
    "Surendranagar",
    "Bharuch",
    "Valsad",
    "Navsari",
    "Porbandar",
    "Amreli",
    "Patan",
    "Palanpur",
    "Himmatnagar",
    "Godhra",
    "Dahod",
    "Botad",
  ],
  Maharashtra: [
    "Mumbai",
    "Pune",
    "Nagpur",
    "Nashik",
    "Aurangabad",
    "Thane",
    "Solapur",
  ],
  Rajasthan: ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Ajmer", "Bikaner"],
  "Madhya Pradesh": ["Bhopal", "Indore", "Gwalior", "Jabalpur", "Ujjain"],
  Delhi: ["New Delhi", "Delhi"],
  Karnataka: ["Bangalore", "Mysore", "Hubli", "Mangalore", "Belgaum"],
  "Tamil Nadu": [
    "Chennai",
    "Coimbatore",
    "Madurai",
    "Tiruchirappalli",
    "Salem",
  ],
  "Uttar Pradesh": [
    "Lucknow",
    "Kanpur",
    "Agra",
    "Varanasi",
    "Allahabad",
    "Meerut",
  ],
};

// ─── Member Category list ─────────────────────────────────────────────────────
const MEMBER_CATEGORIES = [
  "General",
  "Regular",
  "Student",
  "Senior Citizen",
  "Life Member",
  "Institutional",
  "VIP / Patron",
  "Researcher",
  "Staff",
];

const statusStyles: Record<string, string> = {
  active: "badge-success",
  approved: "badge-info",
  suspended: "badge-destructive",
  pending: "badge-warning",
};

// ─── Toggle Switch Component ──────────────────────────────────────────────────
const StatusToggle = ({
  active,
  onChange,
}: {
  active: boolean;
  onChange: (v: boolean) => void;
}) => (
  <button
    type="button"
    onClick={() => onChange(!active)}
    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none ${
      active ? "bg-success" : "bg-muted-foreground/30"
    }`}
    aria-label="Toggle status"
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
        active ? "translate-x-8" : "translate-x-1"
      }`}
    />
  </button>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Members = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Screen view mode
  const [viewMode, setViewMode] = useState<"list" | "add">("list");

  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCity, setFilterCity] = useState("all");

  // Dialog states (View / Edit / Delete remain as dialogs)
  const [viewingMember, setViewingMember] = useState<Member | null>(null);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [cardModalMember, setCardModalMember] = useState<Member | null>(null);
  const [isAutoGenerated, setIsAutoGenerated] = useState(false);

  // Dynamic city list based on selected state
  const [availableCities, setAvailableCities] = useState<string[]>([]);

  // Add Member Form state
  const initialForm = {
    name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    referenceId: "",
    memberCategory: "",
    dateOfJoining: new Date().toISOString().split("T")[0],
    dateOfEnd: "",
    statusActive: true,
    status: "active" as Member["status"],
  };
  const [form, setForm] = useState(initialForm);

  // Auto-generate last member code display
  const { data: members = [], isLoading } = useQuery({
    queryKey: ["members"],
    queryFn: getMembers,
  });

  const lastMemberCode =
    members.length > 0 ? members[members.length - 1].memberId : "—";

  const nextMemberCode =
    members.length > 0
      ? `M${String(members.length + 1).padStart(3, "0")}`
      : "M001";

  // Update city list when state changes
  useEffect(() => {
    if (form.state && CITIES_BY_STATE[form.state]) {
      setAvailableCities(CITIES_BY_STATE[form.state]);
      setForm(prev => ({ ...prev, city: "" }));
    } else {
      setAvailableCities([]);
    }
  }, [form.state]);

  // Sync statusActive → status field
  useEffect(() => {
    setForm(prev => ({
      ...prev,
      status: prev.statusActive ? "active" : "suspended",
    }));
  }, [form.statusActive]);

  const addMutation = useMutation({
    mutationFn: createMember,
    onSuccess: newMember => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      setForm(initialForm);
      setViewMode("list");
      toast({
        title: "Member Registered Successfully",
        description: `${newMember.name} (${newMember.memberId}) registered. Member QR ID card generated automatically!`,
      });
      setCardModalMember(newMember);
      setIsAutoGenerated(true);
    },
    onError: (error: Error) => {
      toast({
        title: "Unable to Register Member",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Member> }) =>
      updateMember(id, data),
    onSuccess: updated => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
      toast({
        title: "Member Updated",
        description: `${updated.name}'s profile updated.`,
      });
      setEditingMember(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Unable to Update Member",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["members"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast({
        title: "Member Deleted",
        description: "Member record removed.",
        variant: "destructive",
      });
      setDeletingId(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Unable to Delete Member",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Filter Logic
  const uniqueCities = Array.from(
    new Set(members.map(m => m.city).filter(Boolean))
  );

  const filtered = members.filter(m => {
    const matchesSearch =
      m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.memberId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.phone.includes(searchQuery) ||
      m.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === "all" || m.status === filterStatus;
    const matchesCity = filterCity === "all" || m.city === filterCity;
    return matchesSearch && matchesStatus && matchesCity;
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({
        title: "Validation Error",
        description: "Name and Phone number are required.",
        variant: "destructive",
      });
      return;
    }
    addMutation.mutate({
      memberId: nextMemberCode,
      name: form.name,
      phone: form.phone,
      email: form.email,
      address: form.address,
      city: form.city,
      state: form.state,
      pincode: form.pincode,
      referenceId: form.referenceId,
      memberCategory: form.memberCategory,
      joinedDate: form.dateOfJoining,
      expiryDate: form.dateOfEnd,
      status: form.status,
    });
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMember) {
      return;
    }
    updateMutation.mutate({ id: editingMember.id, data: editingMember });
  };

  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(filtered);

  return (
    <DashboardLayout
      title="Members Directory"
      subtitle="Manage member registrations, statuses, and borrow history"
    >
      {/* ━━━ LIST VIEW ━━━ */}
      {viewMode === "list" && (
        <div className="animate-fade-in space-y-6">
          {/* Summary Stats Strip */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              {
                label: "Total Members",
                value: members.length,
                color: "text-primary",
              },
              {
                label: "Active",
                value: members.filter(m => m.status === "active").length,
                color: "text-success",
              },
              {
                label: "Pending Approval",
                value: members.filter(m => m.status === "pending").length,
                color: "text-warning",
              },
              {
                label: "Suspended",
                value: members.filter(m => m.status === "suspended").length,
                color: "text-destructive",
              },
            ].map(s => (
              <div
                key={s.label}
                className="rounded-xl border border-border/40 bg-card p-4 shadow-sm"
              >
                <p className="text-xs font-medium text-muted-foreground">
                  {s.label}
                </p>
                <p className={`mt-1 text-2xl font-bold ${s.color}`}>
                  {isLoading ? "—" : s.value}
                </p>
              </div>
            ))}
          </div>

          <div className="form-section">
            {/* Header Row */}
            <div className="mb-6 flex flex-col items-start justify-between gap-4 border-b pb-4 sm:flex-row sm:items-center">
              <div>
                <h2 className="text-lg font-bold text-foreground">
                  Registered Members
                </h2>
                <p className="text-xs text-muted-foreground">
                  Showing {filtered.length} of {members.length} members
                </p>
              </div>
              <Button
                className="btn-primary w-full sm:w-auto"
                onClick={() => {
                  setForm(initialForm);
                  setViewMode("add");
                }}
              >
                <UserPlus className="mr-2 h-4 w-4" /> Add Member
              </Button>
            </div>

            {/* Filters Bar */}
            <div className="mb-6 grid grid-cols-1 gap-3 rounded-xl border border-border/50 bg-muted/30 p-3 sm:grid-cols-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search member name, ID, phone..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="input-field pl-9 text-xs"
                />
              </div>

              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="input-field h-9 text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending Approval</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterCity} onValueChange={setFilterCity}>
                <SelectTrigger className="input-field h-9 text-xs">
                  <SelectValue placeholder="City" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {uniqueCities.map(city => (
                    <SelectItem key={city} value={city}>
                      {city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Members Table */}
            <div className="overflow-hidden rounded-xl border border-border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Member</TableHead>
                    <TableHead>Contact Info</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Books Issued</TableHead>
                    <TableHead>Joined Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 7 }).map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : filtered.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="py-12 text-center text-muted-foreground"
                      >
                        No members matching criteria found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map(member => (
                      <TableRow key={member.id} className="table-row-hover">
                        <TableCell>
                          <div>
                            <p className="text-sm font-semibold text-foreground">
                              {member.name}
                            </p>
                            <p className="font-mono text-xs text-muted-foreground">
                              {member.memberId}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Phone className="h-3 w-3 text-primary" />
                              {member.phone}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Mail className="h-3 w-3 text-primary" />
                              {member.email}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">
                          {member.city}, {member.state}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`text-sm font-bold ${member.booksIssued > 0 ? "text-primary" : "text-muted-foreground"}`}
                          >
                            {member.booksIssued}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {member.joinedDate}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={statusStyles[member.status] ?? ""}
                          >
                            {member.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setCardModalMember(member);
                                setIsAutoGenerated(false);
                              }}
                              title="View & Print Member QR Card"
                              className="text-primary hover:bg-primary/10"
                            >
                              <QrCode className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewingMember(member)}
                              title="View"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingMember(member)}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:bg-destructive/10"
                              onClick={() => setDeletingId(member.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* ━━━ ADD MEMBER FULL SCREEN ━━━ */}
      {viewMode === "add" && (
        <div className="animate-fade-in space-y-6">
          <form onSubmit={handleSave}>
            <div className="form-section">
              {/* Header */}
              <div className="mb-8 flex items-center justify-between gap-4 border-b pb-4">
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="rounded-xl"
                    onClick={() => {
                      setForm(initialForm);
                      setViewMode("list");
                    }}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div>
                    <h2 className="flex items-center gap-2 text-lg font-bold text-foreground">
                      <UserPlus className="h-5 w-5 text-accent" />
                      Register New Member
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Fill in member details to register a new library member
                    </p>
                  </div>
                </div>
              </div>

              {/* ── Last Member Code (Auto Fetch) ── */}
              <div className="mb-6 flex items-center gap-4 rounded-xl border border-accent/20 bg-accent/5 p-4">
                <CreditCard className="h-5 w-5 flex-shrink-0 text-accent" />
                <div className="grid flex-1 grid-cols-2 gap-4">
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      Last Member Code
                    </p>
                    <p className="mt-0.5 font-mono text-sm font-bold text-foreground">
                      {isLoading ? "Loading…" : lastMemberCode}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      New Member Will Get
                    </p>
                    <p className="mt-0.5 font-mono text-sm font-bold text-accent">
                      {isLoading ? "Loading…" : nextMemberCode}
                    </p>
                  </div>
                </div>
              </div>

              {/* Row 1: Full Name & Phone */}
              <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <User className="h-4 w-4 text-accent" />
                    Full Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    placeholder="e.g. Ramesh Patel"
                    className="input-field"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Phone className="h-4 w-4 text-accent" />
                    Phone Number <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    placeholder="10-digit mobile number"
                    className="input-field font-mono"
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    maxLength={10}
                  />
                </div>
              </div>

              {/* Row 2: Email & Member Category */}
              <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Mail className="h-4 w-4 text-accent" />
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    placeholder="ramesh@email.com"
                    className="input-field"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <CreditCard className="h-4 w-4 text-accent" />
                    Member Category
                  </Label>
                  <Select
                    value={form.memberCategory}
                    onValueChange={v => setForm({ ...form, memberCategory: v })}
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue placeholder="Select member category" />
                    </SelectTrigger>
                    <SelectContent>
                      {MEMBER_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 3: Street Address */}
              <div className="mb-6 space-y-2">
                <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <MapPin className="h-4 w-4 text-accent" />
                  Street Address
                </Label>
                <Input
                  placeholder="Flat No. / Street / Area"
                  className="input-field"
                  value={form.address}
                  onChange={e => setForm({ ...form, address: e.target.value })}
                />
              </div>

              {/* Row 4: State → City → Pincode */}
              <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    State
                  </Label>
                  <Select
                    value={form.state}
                    onValueChange={v => setForm({ ...form, state: v })}
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {INDIAN_STATES.map(s => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    City
                  </Label>
                  {availableCities.length > 0 ? (
                    <Select
                      value={form.city}
                      onValueChange={v => setForm({ ...form, city: v })}
                    >
                      <SelectTrigger className="input-field">
                        <SelectValue placeholder="Select city" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {availableCities.map(c => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      placeholder={
                        form.state ? "Enter city" : "Select state first"
                      }
                      className="input-field"
                      value={form.city}
                      onChange={e => setForm({ ...form, city: e.target.value })}
                      disabled={!form.state}
                    />
                  )}
                  {form.state && !CITIES_BY_STATE[form.state] && (
                    <p className="text-[11px] text-muted-foreground">
                      Type your city name
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Pincode
                  </Label>
                  <Input
                    placeholder="380001"
                    className="input-field font-mono"
                    value={form.pincode}
                    onChange={e =>
                      setForm({ ...form, pincode: e.target.value })
                    }
                    maxLength={6}
                  />
                </div>
              </div>

              {/* Row 5: Date of Joining & Date of End */}
              <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Calendar className="h-4 w-4 text-accent" />
                    Date of Joining
                  </Label>
                  <Input
                    type="date"
                    className="input-field"
                    value={form.dateOfJoining}
                    onChange={e =>
                      setForm({ ...form, dateOfJoining: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Date of End (Membership Expiry)
                  </Label>
                  <Input
                    type="date"
                    className="input-field"
                    value={form.dateOfEnd}
                    onChange={e =>
                      setForm({ ...form, dateOfEnd: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Row 6: Reference ID & Status Toggle */}
              <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Reference Member ID
                  </Label>
                  <Input
                    placeholder="e.g. M001 (who referred this member)"
                    className="input-field font-mono"
                    value={form.referenceId}
                    onChange={e =>
                      setForm({ ...form, referenceId: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-foreground">
                    Status
                  </Label>
                  <div className="flex h-10 items-center gap-4 rounded-xl border border-border/50 bg-muted/20 p-3">
                    <StatusToggle
                      active={form.statusActive}
                      onChange={v => setForm({ ...form, statusActive: v })}
                    />
                    <div>
                      <span
                        className={`text-sm font-bold ${
                          form.statusActive
                            ? "text-success"
                            : "text-muted-foreground"
                        }`}
                      >
                        {form.statusActive ? "Active" : "Inactive"}
                      </span>
                      <p className="text-[11px] text-muted-foreground">
                        {form.statusActive
                          ? "Member can borrow books"
                          : "Member access is suspended"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 border-t pt-6">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => {
                    setForm(initialForm);
                    setViewMode("list");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="btn-primary"
                  disabled={addMutation.isPending}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {addMutation.isPending ? "Registering…" : "Register Member"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ━━━ View Member Profile Modal ━━━ */}
      <Dialog
        open={!!viewingMember}
        onOpenChange={open => !open && setViewingMember(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg text-primary">
              {viewingMember?.name}
            </DialogTitle>
            <DialogDescription className="font-mono text-xs">
              Member ID: {viewingMember?.memberId}
            </DialogDescription>
          </DialogHeader>
          {viewingMember && (
            <div className="space-y-3 py-2 text-xs">
              <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted/30 p-3">
                <div>
                  <span className="block text-muted-foreground">Phone</span>
                  <span className="font-semibold">{viewingMember.phone}</span>
                </div>
                <div>
                  <span className="block text-muted-foreground">Email</span>
                  <span className="font-semibold">
                    {viewingMember.email || "—"}
                  </span>
                </div>
                <div className="mt-2">
                  <span className="block text-muted-foreground">
                    Joined Date
                  </span>
                  <span>{viewingMember.joinedDate}</span>
                </div>
                <div className="mt-2">
                  <span className="block text-muted-foreground">Status</span>
                  <span className="font-semibold capitalize">
                    {viewingMember.status}
                  </span>
                </div>
              </div>
              <div className="space-y-1 rounded-lg border bg-card p-3">
                <span className="block font-medium text-muted-foreground">
                  Full Address:
                </span>
                <p className="text-foreground">{viewingMember.address}</p>
                <p className="text-muted-foreground">
                  {viewingMember.city}, {viewingMember.state} -{" "}
                  {viewingMember.pincode}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="block text-muted-foreground">
                    Books Issued
                  </span>
                  <span className="text-sm font-bold text-primary">
                    {viewingMember.booksIssued} books
                  </span>
                </div>
                <div>
                  <span className="block text-muted-foreground">
                    Reference ID
                  </span>
                  <span>{viewingMember.referenceId || "Direct"}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const target = viewingMember;
                setViewingMember(null);
                setCardModalMember(target);
                setIsAutoGenerated(false);
              }}
              className="gap-1.5 border-primary/30 text-primary hover:bg-primary/10"
            >
              <QrCode className="h-4 w-4" /> Print QR Card
            </Button>
            <Button onClick={() => setViewingMember(null)}>
              Close Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ━━━ Edit Member Modal ━━━ */}
      <Dialog
        open={!!editingMember}
        onOpenChange={open => !open && setEditingMember(null)}
      >
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Member Profile</DialogTitle>
          </DialogHeader>
          {editingMember && (
            <form
              onSubmit={handleEditSubmit}
              className="space-y-4 py-2 text-xs"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Full Name</Label>
                  <Input
                    value={editingMember.name}
                    onChange={e =>
                      setEditingMember({
                        ...editingMember,
                        name: e.target.value,
                      })
                    }
                    className="input-field"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Phone</Label>
                  <Input
                    value={editingMember.phone}
                    onChange={e =>
                      setEditingMember({
                        ...editingMember,
                        phone: e.target.value,
                      })
                    }
                    className="input-field"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input
                  value={editingMember.email}
                  onChange={e =>
                    setEditingMember({
                      ...editingMember,
                      email: e.target.value,
                    })
                  }
                  className="input-field"
                />
              </div>
              <div className="space-y-1">
                <Label>Address</Label>
                <Input
                  value={editingMember.address}
                  onChange={e =>
                    setEditingMember({
                      ...editingMember,
                      address: e.target.value,
                    })
                  }
                  className="input-field"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>City</Label>
                  <Input
                    value={editingMember.city}
                    onChange={e =>
                      setEditingMember({
                        ...editingMember,
                        city: e.target.value,
                      })
                    }
                    className="input-field"
                  />
                </div>
                <div className="space-y-1">
                  <Label>State</Label>
                  <Input
                    value={editingMember.state}
                    onChange={e =>
                      setEditingMember({
                        ...editingMember,
                        state: e.target.value,
                      })
                    }
                    className="input-field"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Pincode</Label>
                  <Input
                    value={editingMember.pincode}
                    onChange={e =>
                      setEditingMember({
                        ...editingMember,
                        pincode: e.target.value,
                      })
                    }
                    className="input-field"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Reference ID</Label>
                  <Input
                    value={editingMember.referenceId || ""}
                    onChange={e =>
                      setEditingMember({
                        ...editingMember,
                        referenceId: e.target.value,
                      })
                    }
                    className="input-field"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select
                    value={editingMember.status}
                    onValueChange={v =>
                      setEditingMember({
                        ...editingMember,
                        status: v as Member["status"],
                      })
                    }
                  >
                    <SelectTrigger className="input-field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="pending">Pending Approval</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter className="mt-4 border-t pt-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditingMember(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="btn-primary"
                >
                  {updateMutation.isPending ? "Updating..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ━━━ Delete Confirmation ━━━ */}
      <Dialog
        open={!!deletingId}
        onOpenChange={open => !open && setDeletingId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" /> Delete Member Record
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this member account?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingId && deleteMutation.mutate(deletingId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Confirm Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ━━━ Member QR Card Modal ━━━ */}
      <MemberIDCardModal
        member={cardModalMember}
        isOpen={!!cardModalMember}
        onClose={() => setCardModalMember(null)}
        isAutoGenerated={isAutoGenerated}
      />
    </DashboardLayout>
  );
};

export default Members;
